/**
 * Host Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries';
import type { HostProfile, HostProfileInsert, HostProfileUpdate } from './types';

const QUERY_KEY = 'host_profiles';

// ============================================================
// QUERIES
// ============================================================

export function useHostProfiles() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      // @ts-ignore - API non ancora implementata
      const { data, error } = await api.getHostProfiles({ userId: user?.id });
      if (error) throw error;
      return (data || []) as HostProfile[];
    },
    enabled: !!user?.id,
  });
}

export function useHostProfile(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      // @ts-ignore - API non ancora implementata
      const { data, error } = await api.getHostProfileById(id, user?.id);
      if (error) throw error;
      return data as HostProfile | null;
    },
    enabled: !!id && !!user?.id,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateHostProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profile: HostProfileInsert) => {
      // @ts-ignore - API non ancora implementata
      const { data, error } = await api.createHostProfile(profile, user?.id);
      if (error) throw error;
      return data as HostProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateHostProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: HostProfileUpdate }) => {
      // @ts-ignore - API non ancora implementata
      const { data: result, error } = await api.updateHostProfile(id, data, user?.id);
      if (error) throw error;
      return result as HostProfile;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteHostProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // @ts-ignore - API non ancora implementata
      const { error } = await api.deleteHostProfile(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useSyncHostStruttura() {
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ hostId, strutturaId }: { hostId: string; strutturaId: string }) => {
      // @ts-ignore - API non ancora implementata
      const { error } = await api.syncHostStruttura(hostId, strutturaId, user?.id);
      if (error) throw error;
    },
  });
}
